import type { SegmentData } from '../core';
import type { SvgPathOptions, PathCommand, PathParams } from './types';

/**
 * The parameters of each SVG path command, in the order the `d` attribute
 * lists them. `Z` has none.
 */
const elementDescriptions: Record<string, string[]> = {
  m: ['x', 'y'],
  l: ['x', 'y'],
  h: ['x'],
  v: ['y'],
  c: ['x1', 'y1', 'x2', 'y2', 'x', 'y'],
  s: ['x2', 'y2', 'x', 'y'],
  q: ['x1', 'y1', 'x', 'y'],
  t: ['x', 'y'],
  a: ['rx', 'ry', 'xAr', 'lAf', 'sf', 'x', 'y'],
  z: []
};

/**
 * The axis each parameter is scaled along. An arc's rotation and flags
 * aren't coordinates, so they're left out.
 */
const scaleAxes: Record<string, 'x' | 'y'> = {
  x: 'x',
  x1: 'x',
  x2: 'x',
  rx: 'x',
  y: 'y',
  y1: 'y',
  y2: 'y',
  ry: 'y'
};

/** The axis each parameter is translated along: the points, not an arc's radii. */
const translateAxes: Record<string, 'x' | 'y'> = {
  x: 'x',
  x1: 'x',
  x2: 'x',
  y: 'y',
  y1: 'y',
  y2: 'y'
};

/**
 * Default options for newly created SVG path objects.
 */
const defaultOptions: Required<SvgPathOptions> = {
  // The decimals numbers are rounded to; `false` doesn't round.
  accuracy: 3
};

const commandPattern = /[MmZzLlHhVvCcSsQqTtAa]/y;
const numberPattern = /[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/y;
const flagPattern = /[01]/y;
const separatorPattern = /[\s,]*/y;

/**
 * Reads the commands of an SVG path's `d`: numbers with signs and exponents,
 * numbers without separators (`.5.5`, `1-2`), arc flags written together, and
 * implicit repeats of a command (after a move, as lines).
 */
function parseCommands(path: string): PathCommand[] {
  let index = 0;
  const read = (pattern: RegExp) => {
    separatorPattern.lastIndex = index;
    separatorPattern.exec(path);
    pattern.lastIndex = separatorPattern.lastIndex;
    const match = pattern.exec(path);
    if (match) {
      index = pattern.lastIndex;
    }
    return match?.[0];
  };
  const fail = (): never => {
    throw new Error(
      `Invalid SVG path at position ${index}: "${path.slice(index, index + 20)}"`
    );
  };

  const commands: PathCommand[] = [];
  let command = read(commandPattern) ?? (path.trim() ? fail() : undefined);
  while (command) {
    const names = elementDescriptions[command.toLowerCase()];
    const params: Record<string, number> = {};
    for (const name of names) {
      const value = read(
        name === 'lAf' || name === 'sf' ? flagPattern : numberPattern
      );
      params[name] = value === undefined ? fail() : +value;
    }
    commands.push({ command, ...params } as PathCommand);

    const next = read(commandPattern);
    if (next) {
      command = next;
      continue;
    }
    // A number without a command letter repeats the command; after a move it's a line.
    separatorPattern.lastIndex = index;
    separatorPattern.exec(path);
    index = separatorPattern.lastIndex;
    if (index >= path.length) {
      break;
    }
    if (names.length === 0) {
      fail();
    }
    numberPattern.lastIndex = index;
    if (!numberPattern.test(path)) {
      fail();
    }
    command = command === 'M' ? 'L' : command === 'm' ? 'l' : command;
  }
  return commands;
}

function element(
  command: string,
  params: PathParams,
  pathElements: PathCommand[],
  pos: number,
  relative: boolean,
  data?: SegmentData
) {
  const pathElement: PathCommand = {
    command: relative ? command.toLowerCase() : command.toUpperCase(),
    ...params,
    ...(data ? { data } : {})
  };

  pathElements.splice(pos, 0, pathElement);
}

function forEachParam<T extends PathParams = PathParams>(
  pathElements: PathCommand<T>[],
  cb: (
    cmd: PathCommand<T>,
    param: keyof T,
    cmdIndex: number,
    paramIndex: number,
    cmds: PathCommand<T>[]
  ) => void
) {
  pathElements.forEach((pathElement, pathElementIndex) => {
    elementDescriptions[pathElement.command.toLowerCase()].forEach(
      (paramName, paramIndex) => {
        cb(
          pathElement,
          paramName as keyof PathParams,
          pathElementIndex,
          paramIndex,
          pathElements
        );
      }
    );
  });
}

/**
 * Builds and changes the `d` of an SVG `path`: move, line, curve and arc
 * commands, with a cursor where new commands go. Lines, areas and slices
 * are `SvgPath`s, and interpolations such as {@link Interpolation.cardinal}
 * return one. Each command can carry the data of its point, which the
 * chart reads back for its points.
 *
 * @example
 * ```ts
 * import { SvgPath } from 'chartist';
 *
 * const path = new SvgPath()
 *   .move(0, 100)
 *   .line(50, 20)
 *   .curve(70, 0, 90, 0, 100, 40);
 *
 * path.stringify(); // 'M0,100L50,20C70,0,90,0,100,40'
 *
 * // 10px to the right, at half the height
 * path.translate(10, 0).scale(1, 0.5);
 * ```
 */
export class SvgPath {
  /**
   * Joins paths into a new path, in the order given. The new path gets
   * copies of the commands, and its cursor is at the end.
   * @param paths A list of paths to be joined together. The order is important.
   * @param close If the newly created path should be a closed path
   * @param options Path options for the newly created path.
   */
  static join(paths: SvgPath[], close = false, options?: SvgPathOptions) {
    const joinedPath = new SvgPath(close, options);
    for (let i = 0; i < paths.length; i++) {
      const path = paths[i];
      for (let j = 0; j < path.pathElements.length; j++) {
        joinedPath.pathElements.push({ ...path.pathElements[j] });
      }
    }
    joinedPath.pos = joinedPath.pathElements.length;
    return joinedPath;
  }

  /** The path's commands, in order: each with its letter, its coordinates and the data of its point. */
  pathElements: PathCommand[] = [];
  private pos = 0;
  private options: Required<SvgPathOptions>;

  /**
   * Creates an empty path.
   * @param close Closes the path when it's stringified (a `Z` at the end).
   * @param options The path's options, such as the rounding `accuracy`.
   */
  constructor(
    private readonly close = false,
    options?: SvgPathOptions
  ) {
    this.options = { ...defaultOptions, ...options };
  }

  /**
   * Gets or sets the current position (cursor) inside of the path. You can move around the cursor freely but limited to 0 or the count of existing elements. All modifications with element functions will insert new elements at the position of this cursor.
   * @param pos If a number is passed then the cursor is set to this position in the path element array.
   * @return If the position parameter was passed then the return value will be the path object for easy call chaining. If no position parameter was passed then the current position is returned.
   */
  position(pos: number): this;
  position(): number;
  position(pos?: number) {
    if (pos !== undefined) {
      this.pos = Math.max(0, Math.min(this.pathElements.length, pos));
      return this;
    } else {
      return this.pos;
    }
  }

  /**
   * Removes elements from the path starting at the current position.
   * @param count Number of path elements that should be removed from the current position.
   * @return The current path object for easy call chaining.
   */
  remove(count: number) {
    this.pathElements.splice(this.pos, count);
    return this;
  }

  /**
   * Use this function to add a new move SVG path element.
   * @param x The x coordinate for the move element.
   * @param y The y coordinate for the move element.
   * @param relative If set to true the move element will be created with relative coordinates (lowercase letter)
   * @param data Any data that should be stored with the element object that will be accessible in pathElement
   * @return The current path object for easy call chaining.
   */
  move(x: number, y: number, relative = false, data?: SegmentData) {
    element(
      'M',
      {
        x: +x,
        y: +y
      },
      this.pathElements,
      this.pos++,
      relative,
      data
    );
    return this;
  }

  /**
   * Use this function to add a new line SVG path element.
   * @param x The x coordinate for the line element.
   * @param y The y coordinate for the line element.
   * @param relative If set to true the line element will be created with relative coordinates (lowercase letter)
   * @param data Any data that should be stored with the element object that will be accessible in pathElement
   * @return The current path object for easy call chaining.
   */
  line(x: number, y: number, relative = false, data?: SegmentData) {
    element(
      'L',
      {
        x: +x,
        y: +y
      },
      this.pathElements,
      this.pos++,
      relative,
      data
    );
    return this;
  }

  /**
   * Use this function to add a new curve SVG path element.
   * @param x1 The x coordinate for the first control point of the bezier curve.
   * @param y1 The y coordinate for the first control point of the bezier curve.
   * @param x2 The x coordinate for the second control point of the bezier curve.
   * @param y2 The y coordinate for the second control point of the bezier curve.
   * @param x The x coordinate for the target point of the curve element.
   * @param y The y coordinate for the target point of the curve element.
   * @param relative If set to true the curve element will be created with relative coordinates (lowercase letter)
   * @param data Any data that should be stored with the element object that will be accessible in pathElement
   * @return The current path object for easy call chaining.
   */
  curve(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    x: number,
    y: number,
    relative = false,
    data?: SegmentData
  ) {
    element(
      'C',
      {
        x1: +x1,
        y1: +y1,
        x2: +x2,
        y2: +y2,
        x: +x,
        y: +y
      },
      this.pathElements,
      this.pos++,
      relative,
      data
    );
    return this;
  }

  /**
   * Use this function to add a new non-bezier curve SVG path element.
   * @param rx The radius to be used for the x-axis of the arc.
   * @param ry The radius to be used for the y-axis of the arc.
   * @param xAr Defines the orientation of the arc
   * @param lAf Large arc flag
   * @param sf Sweep flag
   * @param x The x coordinate for the target point of the curve element.
   * @param y The y coordinate for the target point of the curve element.
   * @param relative If set to true the curve element will be created with relative coordinates (lowercase letter)
   * @param data Any data that should be stored with the element object that will be accessible in pathElement
   * @return The current path object for easy call chaining.
   */
  arc(
    rx: number,
    ry: number,
    xAr: number,
    lAf: number,
    sf: number,
    x: number,
    y: number,
    relative = false,
    data?: SegmentData
  ) {
    element(
      'A',
      {
        rx,
        ry,
        xAr,
        lAf,
        sf,
        x,
        y
      },
      this.pathElements,
      this.pos++,
      relative,
      data
    );
    return this;
  }

  /**
   * Parses the `d` of an SVG path and inserts its commands at the cursor. A
   * closing `Z` at the end is dropped: the `close` of the path object decides
   * that.
   * @param path An SVG path of any commands (`M`, `L`, `H`, `V`, `C`, `S`, `Q`, `T`, `A`, `Z`), absolute or relative.
   * @return The current path object for easy call chaining.
   * @throws An `Error` if the path isn't valid SVG path syntax.
   */
  parse(path: string) {
    const elements = parseCommands(path);

    // The path's own `close` decides about a closing Z at the end.
    if (elements.at(-1)?.command.toUpperCase() === 'Z') {
      elements.pop();
    }

    this.pathElements.splice(this.pos, 0, ...elements);
    this.pos += elements.length;

    return this;
  }

  /**
   * Writes the path as a string for the `d` attribute of a `path` element.
   * Numbers are rounded to the `accuracy` option's decimals (to integers
   * with `0`, not at all with `false`), and a closed path ends with `Z`.
   */
  stringify() {
    const { accuracy } = this.options;
    const accuracyMultiplier = Math.pow(10, accuracy || 0);

    return (
      this.pathElements.reduce((path, pathElement) => {
        const params = elementDescriptions[
          pathElement.command.toLowerCase()
        ].map(paramName => {
          const value = pathElement[paramName as keyof PathCommand] as number;

          return typeof accuracy === 'number'
            ? Math.round(value * accuracyMultiplier) / accuracyMultiplier
            : value;
        });

        return path + pathElement.command + params.join(',');
      }, '') + (this.close ? 'Z' : '')
    );
  }

  /**
   * Scales all elements in the current SVG path object. There is an individual parameter for each coordinate. Scaling will also be done for control points of curves and the radii of arcs, affecting the given coordinate; an arc's rotation and flags stay as they are.
   * @param x The number which will be used to scale the x, x1, x2 and rx of all path elements.
   * @param y The number which will be used to scale the y, y1, y2 and ry of all path elements.
   * @return The current path object for easy call chaining.
   */
  scale(x: number, y: number) {
    forEachParam(this.pathElements, (pathElement, paramName) => {
      const axis = scaleAxes[paramName as string];
      if (axis) {
        pathElement[paramName] *= axis === 'x' ? x : y;
      }
    });
    return this;
  }

  /**
   * Translates all elements in the current SVG path object. The translation is relative and there is an individual parameter for each coordinate. Translation will also be done for control points of curves, affecting the given coordinate; an arc's radii, rotation and flags stay as they are.
   * @param x The number which will be used to translate the x, x1 and x2 of all path elements.
   * @param y The number which will be used to translate the y, y1 and y2 of all path elements.
   * @return The current path object for easy call chaining.
   */
  translate(x: number, y: number) {
    forEachParam(this.pathElements, (pathElement, paramName) => {
      const axis = translateAxes[paramName as string];
      if (axis) {
        pathElement[paramName] += axis === 'x' ? x : y;
      }
    });
    return this;
  }

  /**
   * This function will run over all existing path elements and then loop over their attributes. The callback function will be called for every path element attribute that exists in the current path.
   * The method signature of the callback function looks like this:
   * ```javascript
   * function(pathElement, paramName, pathElementIndex, paramIndex, pathElements)
   * ```
   * If something else than undefined is returned by the callback function, this value will be used to replace the old value. This allows you to build custom transformations of path objects that can't be achieved using the basic transformation functions scale and translate.
   * @param transformFnc The callback function for the transformation. Check the signature in the function description.
   * @return The current path object for easy call chaining.
   */
  transform(
    transformFnc: <T extends PathParams = PathParams>(
      cmd: PathCommand<T>,
      param: keyof T,
      cmdIndex: number,
      paramIndex: number,
      cmds: PathCommand<T>[]
    ) => number | void
  ) {
    forEachParam(
      this.pathElements,
      (pathElement, paramName, pathElementIndex, paramIndex, pathElements) => {
        const transformed = transformFnc(
          pathElement,
          paramName,
          pathElementIndex,
          paramIndex,
          pathElements
        );
        if (transformed || transformed === 0) {
          pathElement[paramName] = transformed;
        }
      }
    );
    return this;
  }

  /**
   * This function clones a whole path object with all its properties. This is a deep clone and path element objects will also be cloned.
   * @param close Optional option to set the new cloned path to closed. If not specified or false, the original path close option will be used.
   */
  clone(close = false) {
    const clone = new SvgPath(close || this.close);
    clone.pos = this.pos;
    clone.pathElements = this.pathElements
      .slice()
      .map(pathElement => ({ ...pathElement }));
    clone.options = { ...this.options };
    return clone;
  }

  /**
   * Splits the path into new paths, each starting at the given command,
   * such as `'M'` to get the separate pieces of a line with holes. The
   * letter matches absolute and relative commands. The pieces get copies of
   * the commands and the path's options, with their cursor at the end; the
   * last piece is closed if the path is.
   * @param command The command letter to split at.
   */
  splitByCommand(command: string) {
    const letter = command.toUpperCase();
    const pieces: PathCommand[][] = [[]];

    this.pathElements.forEach(pathElement => {
      if (
        pathElement.command.toUpperCase() === letter &&
        pieces[pieces.length - 1].length !== 0
      ) {
        pieces.push([]);
      }

      pieces[pieces.length - 1].push({ ...pathElement });
    });

    return pieces.map((pathElements, index) => {
      const piece = new SvgPath(
        this.close && index === pieces.length - 1,
        this.options
      );
      piece.pathElements = pathElements;
      piece.pos = pathElements.length;
      return piece;
    });
  }
}
